import React from 'react';

import { config } from 'config/config';
import moment from 'moment';
import {
  buildBeautyGuide,
  buildChallengeCertificate,
  buildChallengeCheckout,
  buildChallengeCheckoutPayment,
  buildChallengeCheckoutBookingTimify,
  buildChallengeCheckoutDonation,
  buildChallengeCheckoutLottery,
  buildChallengeEvents,
  buildChallengeGallery,
  buildChallengeHidden,
  buildChallengeIncludedItemsList,
  buildChallengeMaterials,
  buildChallengeQuiz,
  buildChallengeQuizDermo,
  buildChallengeQuizTypeform,
  buildChallengeRedeemProduct,
  buildChallengeSpeakers,
  buildChallengeSteps,
  buildChallengeText,
  buildChallengeTextDuration,
  buildChallengeTextLink,
  buildChallengeVideo,
  buildChallengeVideoSection,
  buildChallengeWysiwyg,
  buildExperienceActivator,
  buildExperienceProgress,
  buildHeadboard,
  buildLinkedChallenges,
} from 'src/components/challenge/builder';
import ChallengeActivationButton from 'src/components/challenge/ChallengeActivationButton';
import ChallengeCheckout from 'src/components/challenge/ChallengeCheckout';
import ChallengeCheckoutDonation from 'src/components/challenge/ChallengeCheckoutDonation';
import ChallengeExperienceProgress from 'src/components/challenge/ChallengeExperienceProgress';
import ChallengeHidden from 'src/components/challenge/ChallengeHidden';
import ChallengeIncludedItems from 'src/components/challenge/ChallengeIncludedItems';
import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import ChallengeLinkedChallenges from 'src/components/challenge/ChallengeLinkedChallenges';
import ChallengeLinkedText from 'src/components/challenge/ChallengeLinkedText';
import ChallengeMaterials from 'src/components/challenge/ChallengeMaterials';
import CheckoutLottery from 'src/components/experienceCheckout/CheckoutLottery';
// TODO: Add an index.tsx
import ChallengeCheckoutPayment from 'src/components/challenge/ChallengeCheckoutPayment';
import ChallengeCheckoutTimify from 'src/components/challenge/ChallengeCheckoutTimify';
import ChallengeQuiz from 'src/components/challenge/ChallengeQuiz';
import ChallengeQuizTypeform from 'src/components/challenge/ChallengeQuizTypeform';
import ChallengeRedeemProduct from 'src/components/challenge/ChallengeRedeemProduct';
import ChallengeResourceGuide from 'src/components/challenge/ChallengeResourceGuide';
import ChallengeSteps from 'src/components/challenge/ChallengeSteps';
import ChallengeSurvey from 'src/components/challenge/ChallengeSurvey';
import ChallengeText from 'src/components/challenge/ChallengeText';
import ChallengeTextDuration from 'src/components/challenge/ChallengeTextDuration';
import ChallengeVideo from 'src/components/challenge/ChallengeVideo';
import ChallengeVideoSection from 'src/components/challenge/ChallengeVideoSection';
import ChallengeWysiwyg from 'src/components/challenge/ChallengeWYSIWYG';
import ContestBeautyGuide from 'src/components/challenge/contest/ContestBeautyGuide';
import ExperienceActivator from 'src/components/challenge/ExperienceActivator';
import FormationQuizDermo from 'src/components/challenge/formation/FormationQuizDermo';
import ShoppingCart from 'src/components/shoppingCart/ShoppingCart';
import {
  CampaignModuleStatusEnum,
  ChallengeTypeEnum,
  IResourceBehaviorsEnum,
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
  UserResourceEnum,
} from 'src/shared/enums';
import {
  ChallengeDetail,
  IChallenge,
  IChallengeCheckout,
  IChallengeCheckoutBookingTimify,
  IChallengeCheckoutDonation,
  IChallengeCheckoutLottery,
  IChallengeCheckoutPayment,
  IChallengeCoverVideo,
  IChallengeDates,
  IChallengeQuiz,
  IChallengeQuizTypeform,
  IChallengeRedeem,
  IChallengeResource,
  IChallengeResourceConfig,
  IChallengeSpeaker,
  IChallengeVideoSection,
  IExperienceProgress,
  IGallery,
  isVideoValidFormat,
  IUser,
  TypeChallengeModality,
  TypeChallengeStatusLabel,
  TypeChallengeUserLabel,
} from 'src/shared/models';
import { IBeautyGuideResource } from 'src/shared/models/beautyGuideResource.model';
import {
  checkCampaignActive,
  checkExperienceStock,
  checkUserStatus,
  constructResources,
  getResourceReplays,
  isChallengeAvailable,
  isDependentConditionSatisfied,
  isEventInProgress,
} from 'utils';

const { DEFAULT_TIMEZONE } = config.APP;

const {
  ACTIVATION_BUTTON,
  CHALLENGE_MATERIALS,
  CHECKOUT,
  EVENT,
  EXPERIENCE_ACTIVATION,
  EXPERIENCE_PROGRESS,
  FAQ,
  GALLERY,
  INSCRIPTION,
  LINKED_CHALLENGES,
  PARTICIPATION,
  QUIZ,
  QUIZ_DERMO,
  QUIZ_DERMO_PARTICIPATION,
  FORMATION_CERTIFICATE,
  REDEEM_PRODUCT,
  RESOURCES_GUIDE,
  SHOPPING_CART,
  SPEAKER,
  STEPS,
  SURVEY,
  TEXT,
  TEXT_DURATION,
  TEXT_LINK,
  VIDEO,
  QUIZ_TYPEFORM,
  VIDEO_SECTION,
  WYSIWYG,
  INCLUDED_ITEMS_LIST,
  CHECKOUT_LOTTERY,
  CHECKOUT_DONATION,
  BEAUTY_GUIDE,
  HIDDEN,
  CHECKOUT_BOOKING_TIMIFY,
  CHECKOUT_PAYMENT_GATEWAY,
} = ResourceTypeEnum;

const { RESOURCE_GUIDE_DESCRIPTION, RESOURCE_GUIDE_SCORE } =
  ResourceTypeDetailEnum;

const { INACTIVE, DONE, IN_PROGRESS, WAITING_LIST } = UserResourceEnum;

const { FORMATION, CAMPAIGN, TALK, FORMATION_CON } = ChallengeTypeEnum;

const getMultiEvents = (resources: IResourceList[]) => {
  return resources.filter(
    (ele) => ele.idResourceType.idResourceType === ResourceTypeEnum.EVENT,
  );
};

export const builderChallenge = (
  challenge: IChallenge,
  user: IUser,
): ChallengeDetail => {
  const {
    active,
    availableReplays,
    brand: challengeBrand,
    categories,
    code: challengeCode,
    coverImage,
    coverImageMobile,
    coverVideo,
    description,
    descriptionLong,
    descriptionShort,
    disableHeader,
    disableImageHeader,
    endDate,
    endImage,
    endVisDate,
    featured,
    i18nList,
    idChallenge,
    idChallengeType,
    likesList,
    name,
    resourceList,
    showPoints,
    startDate,
    startVisDate,
    specialties,
    roles,
    tags,
  } = challenge;

  let userStatus: { idResource: number; status: number };
  let challengeQuiz: IChallengeQuiz;
  let challengeText = [];
  let challengeVideo: IChallengeCoverVideo = null;

  const challengeType: ChallengeTypeEnum = idChallengeType.idChallengeType;
  let challengeModality: TypeChallengeModality;
  let challengeStatusLabel: TypeChallengeStatusLabel = null;
  let challengeUserLabel: TypeChallengeUserLabel = null;

  let challengePoints = 0;
  let challengePrice = null
  let challengeDonations: number[] = [];
  let challengeFaqs: IResourceList;
  let challengeGallery: IGallery;
  // TODO: rename as "IChallengeModule"
  const challengeResources: IChallengeResource[] = [];
  let challengeResourceGuide: IChallengeResource;

  const sortedResources = constructResources(resourceList);
  const events = getMultiEvents(sortedResources);

  const isExperience = challengeType == ChallengeTypeEnum.EXPERIENCE;
  const isContest = challengeType === ChallengeTypeEnum.CONTEST;
  const isDonation = challengeType === ChallengeTypeEnum.DONATION;
  const isFormationCon = challengeType === ChallengeTypeEnum.FORMATION_CON;

  //TODO: Construir eventos con builderChallenge
  // TODO: Remove headboardConfiguration and use `challangeQuiz `
  const headboardConfiguration = buildHeadboard(challenge);
  const challengeSpeakers: IChallengeSpeaker[] = [];
  const challengeEvents = buildChallengeEvents(challenge, events);
  const mainEvent = challengeEvents.find((event) => event.isMain);
  const challengeDates: IChallengeDates = {
    startDate: moment
      .utc(startDate)
      .utcOffset(DEFAULT_TIMEZONE, false)
      .toLocaleString(),
    endDate: moment
      .utc(endDate)
      .utcOffset(DEFAULT_TIMEZONE, false)
      .toLocaleString(),
    startVisDate: moment.utc(startVisDate).utcOffset(DEFAULT_TIMEZONE, false),
    endVisDate: moment.utc(endVisDate).utcOffset(DEFAULT_TIMEZONE, false),
  };
  const lastQuizDermo = (): number => {
    const quizDermoList = sortedResources.filter((resource: IResourceList) => {
      return (
        resource.idResourceType.idResourceType === QUIZ_DERMO && resource.status
      );
    });
    const lastQuizDermoIndex = quizDermoList.length - 1;

    return quizDermoList[lastQuizDermoIndex].idResource;
  };

  sortedResources.forEach((_resource: IResourceList) => {
    const resourceMainConfig: IChallengeResourceConfig = {
      title: _resource.name,
      isCampaignActivator: _resource.activate,
      isCompleted: checkUserStatus(_resource, [
        CampaignModuleStatusEnum.GENERIC_DONE,
      ]),
      isPartialDone: checkUserStatus(_resource, [
        CampaignModuleStatusEnum.GENERIC_DONE,
      ]),
      isActive: false,
      isBlocked: false,
      isVisible: true,
      isChallengeInDate: isEventInProgress(
        moment.utc(startDate).utcOffset(DEFAULT_TIMEZONE, false),
        moment.utc(endDate).utcOffset(DEFAULT_TIMEZONE, false),
      ),
      points: _resource.score?.points || 0,
      realizedReplays: getResourceReplays(_resource),
    };

    switch (_resource.idResourceType.idResourceType) {
      case RESOURCES_GUIDE:
        challengeResourceGuide = {
          idResource: _resource.idResource,
          key: `challenge--resource-guide-${_resource.idResource}`,
          type: RESOURCES_GUIDE,
          config: {
            title: _resource.name,
            description: _resource.resourceDetailList.find(
              (resourceDetail) =>
                resourceDetail.idResourceTypeD.idResourceTypeD ===
                RESOURCE_GUIDE_DESCRIPTION,
            ).value,
            showPoints: Boolean(
              _resource.resourceDetailList.find(
                (resourceDetail) =>
                  resourceDetail.idResourceTypeD.idResourceTypeD ===
                  RESOURCE_GUIDE_SCORE,
              ).value,
            ),
            showHeader: Boolean(!disableHeader),
            showImageHeader: Boolean(!disableImageHeader),
          },
          resource: _resource,
          render: () => <ChallengeResourceGuide />,
        };
        break;
      case SPEAKER:
        const speaker = buildChallengeSpeakers(_resource);
        challengeSpeakers.push(speaker);
        break;
      case ACTIVATION_BUTTON:
        challengePoints += resourceMainConfig.points;
        challengeResources.push({
          key: `challenge--activation-button-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: ACTIVATION_BUTTON,
          config: resourceMainConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => (
            <ChallengeActivationButton
              {...{
                challengeType: challengeType,
                config: resourceMainConfig,
                idResource: _resource.idResource,
                updateChallenge,
                updateUserPoints,
              }}
            />
          ),
        });
        break;
      case CHALLENGE_MATERIALS:
        const configMaterials = {
          ...resourceMainConfig,
          ...buildChallengeMaterials(_resource, user),
        };
        challengePoints += configMaterials.points;

        challengeResources.push({
          key: `challenge--material-${_resource.idResource}`,
          idResource: _resource.idResource,
          config: configMaterials,
          type: CHALLENGE_MATERIALS,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeMaterials
                {...{
                  challengeType: challengeType,
                  config: configMaterials,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case EVENT:
        challengeEvents.forEach((event) => {
          challengeResources.push({
            key: `challenge--event-${event.idResource}`,
            idResource: event.idResource,
            config: event,
            type: EVENT,
            resource: _resource,
            render: () => <React.Fragment />,
          });
        });

        // TODO: Eliminar porque los eventos ya se construyen con ``buildChallengeEvents``
        if (_resource.resourceDetailList?.length) {
          _resource.resourceList?.forEach((eventResource) => {
            switch (eventResource.idResourceType.idResourceType) {
              case INSCRIPTION:
                if (eventResource.usersM2MList?.length) {
                  const { usersM2MList } = eventResource;
                  const userInscriptions = usersM2MList.filter(
                    (_m2m) => _m2m.status && _m2m.statusResource !== INACTIVE,
                  );
                  if (userInscriptions.length)
                    userStatus = {
                      idResource: eventResource.idResource,
                      status: userInscriptions[0].statusResource,
                    };
                }
                break;
            }
          });
        }
        break;
      case FAQ:
        challengeFaqs = _resource;
        break;
      case GALLERY:
        challengeGallery = buildChallengeGallery(_resource, idChallenge);
        break;
      case LINKED_CHALLENGES:
        const configLinkedChallenge = {
          ...resourceMainConfig,
          ...buildLinkedChallenges(_resource, user),
        };

        challengePoints += configLinkedChallenge.points;

        challengeResources.push({
          key: `challenge--linked-challenge-${_resource.idResource}`,
          idResource: _resource.idResource,
          config: configLinkedChallenge,
          type: LINKED_CHALLENGES,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeLinkedChallenges
                {...{
                  challengeType: challengeType,
                  config: configLinkedChallenge,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case QUIZ:
        challengeQuiz = {
          ...resourceMainConfig,
          ...buildChallengeQuiz(
            _resource,
            idChallenge,
            challengeType,
            mainEvent,
            challengeDates,
          ),
        };
        challengePoints += challengeQuiz.points;

        challengeResources.push({
          key: `challenge--quiz-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: QUIZ,
          config: challengeQuiz,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeQuiz
                {...{
                  challengeType: challengeType,
                  config: challengeQuiz,
                  resource: _resource,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });

        _resource.resourceList.forEach((r) => {
          if (r.idResourceType.idResourceType === PARTICIPATION) {
            const isEvent = challengeType === TALK;
            if (!isEvent)
              userStatus = { idResource: r.idResource, status: null };
            if (r.usersM2MList?.length) {
              const { usersM2MList } = r;
              const userParticipation = usersM2MList.filter(
                (_m2m) => _m2m.status && _m2m.statusResource !== INACTIVE,
              );
              if (userParticipation.length && !isEvent)
                userStatus = {
                  ...userStatus,
                  status: userParticipation[0].statusResource,
                };
            }
          }
        });
        break;
      case QUIZ_DERMO:
        let challengeQuizDermo = {
          ...resourceMainConfig,
          ...buildChallengeQuizDermo(_resource),
        };

        challengePoints += challengeQuizDermo.points;

        // ? Get userStatus
        const quizParticipation = _resource.resourceList.find(
          (resource: IResourceList) => {
            return (
              resource.idResourceType.idResourceType ===
              QUIZ_DERMO_PARTICIPATION
            );
          },
        );

        if (quizParticipation.usersM2MList.length) {
          challengeQuizDermo.isCompleted =
            quizParticipation.usersM2MList[0].status &&
            quizParticipation.usersM2MList[0].statusResource === DONE;

          challengeQuizDermo.isPartialDone =
            quizParticipation.usersM2MList[0].status &&
            quizParticipation.usersM2MList[0].statusResource === IN_PROGRESS;
        }

        // ? Set which is the last Quiz (Module) of the Challenge
        if (_resource.idResource === lastQuizDermo())
          challengeQuizDermo.isLastQuiz = true;

        challengeResources.push({
          key: `challenge--quiz-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: QUIZ_DERMO,
          config: challengeQuizDermo,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <FormationQuizDermo
                {...{
                  challengeType: challengeType,
                  config: challengeQuizDermo,
                  resource: _resource,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });

        break;
      case FORMATION_CERTIFICATE:
        const challengeCertificate = {
          ...resourceMainConfig,
          ...buildChallengeCertificate(_resource),
        };

        challengeResources.push({
          key: `challenge--formation-certificate-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: FORMATION_CERTIFICATE,
          config: challengeCertificate,
          resource: _resource,
          render: () => <React.Fragment />,
        });

        break;
      case SHOPPING_CART:
        challengePoints += resourceMainConfig.points;
        // TODO: Add "config" to implement dependecies
        const shoppingCart = {
          ...resourceMainConfig,
          icon: 'campaign-material-request',
        };

        resourceMainConfig.isCompleted = checkUserStatus(_resource, [
          CampaignModuleStatusEnum.GENERIC_DONE,
          CampaignModuleStatusEnum.SHOPPING_CART_ERROR,
          CampaignModuleStatusEnum.SHOPPING_CART_IN_PROGRESS,
        ]);

        challengeResources.push({
          key: `challenge--shopping-cart-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: SHOPPING_CART,
          resource: _resource,
          config: shoppingCart,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ShoppingCart
                {...{
                  challengeType: challengeType,
                  config: resourceMainConfig,
                  resource: _resource,
                  updateChallenge,
                  updateUserPoints,
                }}
              />
            );
          },
        });
        break;
      case SURVEY:
        const surveyConfig: IChallengeResourceConfig = {
          ...resourceMainConfig,
          isCompleted: Boolean(
            _resource.survey?.userResponseList?.length &&
              _resource.survey.userResponseList[0].status &&
              _resource.survey.userResponseList[0].statusSurvey ===
                CampaignModuleStatusEnum.GENERIC_DONE,
          ),
          points: _resource.survey?.score?.points || 0,
          icon: 'survey',
        };
        challengePoints += surveyConfig.points;

        challengeResources.push({
          key: `challenge--survey-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: SURVEY,
          resource: _resource,
          // TODO: Add "config" to align dependecies
          config: surveyConfig,
          render: ({ updateChallenge, updateUserPoints, performSurvey }) => {
            return (
              <ChallengeSurvey
                key={`challenge--survey-${_resource.idResource}`}
                {...{
                  ...surveyConfig,
                  challengeType: challengeType,
                  isCampaignComponent:
                    challenge.idChallengeType?.idChallengeType === CAMPAIGN,
                  isChallengeComponent: true,
                  performAction: performSurvey,
                  resource: _resource,
                  updateChallenge,
                  updateUserPoints,
                }}
              />
            );
          },
        });

        break;
      case TEXT:
        const configText = {
          ...resourceMainConfig,
          ...buildChallengeText(_resource),
        };

        challengeResources.push({
          key: `challenge--text-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: TEXT,
          config: configText,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeText
                {...{
                  challengeType: challengeType,
                  config: configText,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });

        challengeText = [
          ...challengeText,
          _resource.resourceDetailList[0].value,
        ];
        break;
      case VIDEO:
        const configVideo = {
          ...resourceMainConfig,
          ...buildChallengeVideo(_resource),
        };
        challengePoints += configVideo.points;

        challengeResources.push({
          key: `challenge--video-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: VIDEO,
          config: configVideo,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeVideo
                {...{
                  challengeType: challengeType,
                  config: configVideo,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case EXPERIENCE_PROGRESS:
        const configProgress: IExperienceProgress = {
          ...resourceMainConfig,
          ...buildExperienceProgress(_resource),
        };

        challengeResources.push({
          key: `challenge--experience-progress-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: EXPERIENCE_PROGRESS,
          config: configProgress,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeExperienceProgress
                {...{
                  challengeType: challengeType,
                  config: configProgress,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;

      case STEPS:
        const configSteps = {
          ...resourceMainConfig,
          ...buildChallengeSteps(_resource),
        };

        challengeResources.push({
          key: `challenge--steps-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: STEPS,
          config: configSteps,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeSteps
                {...{
                  challengeType: challengeType,
                  config: configSteps,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case REDEEM_PRODUCT:
        const configRedeem: IChallengeRedeem = {
          ...resourceMainConfig,
          ...buildChallengeRedeemProduct(_resource),
        };
        challengePoints += configRedeem.points;

        challengeResources.push({
          key: `challenge--redeem-product-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: REDEEM_PRODUCT,
          config: configRedeem,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeRedeemProduct
                {...{
                  challengeType: challengeType,
                  config: configRedeem,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case CHECKOUT:
        const checkoutConfig: IChallengeCheckout = {
          ...resourceMainConfig,
          isBlocked: checkExperienceStock(challenge),
          isVisible: isChallengeAvailable(challenge.roles, user),
          ...buildChallengeCheckout(_resource),
        };
        challengePoints += checkoutConfig.points;

        challengeResources.push({
          key: `challenge--checkout-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: CHECKOUT,
          config: checkoutConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeCheckout
                {...{
                  challengeType: challengeType,
                  config: checkoutConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case CHECKOUT_PAYMENT_GATEWAY:
        const checkoutPaymentConfig: IChallengeCheckoutPayment = {
          ...resourceMainConfig,
          isBlocked: checkExperienceStock(challenge),
          isVisible: isChallengeAvailable(challenge.roles, user),
          ...buildChallengeCheckoutPayment(_resource),
        };
        challengePoints += checkoutPaymentConfig.points;
        challengePrice += checkoutPaymentConfig.money

        challengeResources.push({
          key: `challenge--checkout-payment-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: CHECKOUT_PAYMENT_GATEWAY,
          config: checkoutPaymentConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeCheckoutPayment
                {...{
                  challengeType: challengeType,
                  config: checkoutPaymentConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case CHECKOUT_DONATION:
        const checkoutDonationConfig: IChallengeCheckoutDonation = {
          ...resourceMainConfig,
          isVisible: isChallengeAvailable(challenge.roles, user),
          ...buildChallengeCheckoutDonation(_resource),
        };
        challengePoints += checkoutDonationConfig.points;
        challengeDonations = checkoutDonationConfig.donations;

        challengeResources.push({
          key: `challenge--checkout-donation-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: CHECKOUT_DONATION,
          config: checkoutDonationConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeCheckoutDonation
                {...{
                  challengeType: challengeType,
                  config: checkoutDonationConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case CHECKOUT_LOTTERY:
        const checkoutLotteryConfig: IChallengeCheckoutLottery = {
          ...resourceMainConfig,
          isVisible: isChallengeAvailable(challenge.roles, user),
          ...buildChallengeCheckoutLottery(_resource, user),
        };
        challengePoints += checkoutLotteryConfig.points;

        challengeResources.push({
          key: `challenge--checkout-lottery-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: CHECKOUT_LOTTERY,
          config: checkoutLotteryConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <CheckoutLottery
                {...{
                  challengeType: challengeType,
                  config: checkoutLotteryConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case QUIZ_TYPEFORM:
        if (!resourceMainConfig.isChallengeInDate)
          resourceMainConfig.isVisible = false;

        const quizTypeformConfig: IChallengeQuizTypeform = {
          ...resourceMainConfig,
          ...buildChallengeQuizTypeform(_resource),
        };
        challengePoints += quizTypeformConfig.points;

        challengeResources.push({
          key: `challenge--quiz_typeform-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: QUIZ_TYPEFORM,
          config: quizTypeformConfig,
          resource: _resource,
          render: function quizTypeform({ updateChallenge, updateUserPoints }) {
            return (
              <ChallengeQuizTypeform
                {...{
                  challengeType: challengeType,
                  config: quizTypeformConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case EXPERIENCE_ACTIVATION:
        const experienceActivationConfig = {
          ...resourceMainConfig,
          ...buildExperienceActivator(_resource),
        };
        challengeResources.push({
          key: `challenge--experience-activation-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: EXPERIENCE_ACTIVATION,
          config: experienceActivationConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ExperienceActivator
                {...{
                  challengeType: challengeType,
                  config: experienceActivationConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case VIDEO_SECTION:
        const videoSectionConfig: IChallengeVideoSection = {
          ...resourceMainConfig,
          ...buildChallengeVideoSection(_resource),
        };
        challengeResources.push({
          key: `challenge-video-section-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: VIDEO_SECTION,
          config: videoSectionConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeVideoSection
                {...{
                  challengeType,
                  config: videoSectionConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case WYSIWYG:
        const configWysiwyg = {
          ...resourceMainConfig,
          ...buildChallengeWysiwyg(_resource),
        };

        challengeResources.push({
          key: `challenge--wysiwyg-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: WYSIWYG,
          config: configWysiwyg,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeWysiwyg
                {...{
                  challengeType: challengeType,
                  config: configWysiwyg,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case TEXT_LINK:
        const configLinkedText = {
          ...resourceMainConfig,
          ...buildChallengeTextLink(_resource),
        };
        challengeResources.push({
          key: `challenge--linked-text-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: TEXT_LINK,
          config: configLinkedText,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeLinkedText
                {...{
                  challengeType,
                  config: configLinkedText,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case HIDDEN:
        const configHidden = {
          ...resourceMainConfig,
          isVisible: !isChallengeAvailable(challenge.roles, user),
          ...buildChallengeHidden(_resource),
        };
        challengeResources.push({
          key: `challenge--hidden-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: HIDDEN,
          config: configHidden,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeHidden
                {...{
                  challengeType,
                  config: configHidden,
                  updateUserPoints,
                  updateChallenge,
                  user,
                }}
              />
            );
          },
        });
        break;
      case INCLUDED_ITEMS_LIST:
        const configIncludedItems = {
          ...resourceMainConfig,
          ...buildChallengeIncludedItemsList(_resource),
        };

        challengeResources.push({
          key: `challenge--included-items-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: INCLUDED_ITEMS_LIST,
          config: configIncludedItems,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeIncludedItems
                {...{
                  challengeType,
                  configItem: configIncludedItems,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case TEXT_DURATION:
        const configTextDuration = {
          ...resourceMainConfig,
          ...buildChallengeTextDuration(_resource),
        };

        challengeResources.push({
          key: `challenge--duration-text-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: TEXT_DURATION,
          config: configTextDuration,
          resource: _resource,
          render: () => {
            return (
              <ChallengeTextDuration
                {...{
                  config: configTextDuration,
                }}
              />
            );
          },
        });
        break;
      case CHECKOUT_BOOKING_TIMIFY:
        const configCheckoutBookingTimify: IChallengeCheckoutBookingTimify = {
          ...resourceMainConfig,
          isVisible: isChallengeAvailable(challenge.roles, user),
          ...buildChallengeCheckoutBookingTimify(_resource),
        };
        challengePoints += configCheckoutBookingTimify.points;
        challengeResources.push({
          key: `challenge--checkout-booking-timify-text-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: CHECKOUT_BOOKING_TIMIFY,
          config: configCheckoutBookingTimify,
          resource: _resource,

          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ChallengeCheckoutTimify
                {...{
                  config: configCheckoutBookingTimify,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
      case BEAUTY_GUIDE:
        const beautyGuideConfig: IBeautyGuideResource = {
          ...resourceMainConfig,
          ...buildBeautyGuide(_resource),
        };
        challengePoints += beautyGuideConfig.points;

        challengeResources.push({
          key: `challenge--checkout-lottery-${_resource.idResource}`,
          idResource: _resource.idResource,
          type: BEAUTY_GUIDE,
          config: beautyGuideConfig,
          resource: _resource,
          render: ({ updateChallenge, updateUserPoints }) => {
            return (
              <ContestBeautyGuide
                {...{
                  challengeType: challengeType,
                  config: beautyGuideConfig,
                  updateUserPoints,
                  updateChallenge,
                }}
              />
            );
          },
        });
        break;
    }
  });
  const isChallengeReplayable =
    availableReplays > 0 &&
    (isExperience || isDonation) &&
    challengeResources
      .filter((resource) =>
        [
          REDEEM_PRODUCT,
          CHECKOUT,
          CHECKOUT_LOTTERY,
          CHECKOUT_PAYMENT_GATEWAY,
          CHECKOUT_DONATION,
          QUIZ_TYPEFORM,
          CHECKOUT_BOOKING_TIMIFY,
        ].includes(resource.type),
      )
      .every(
        ({ config: { isCompleted, realizedReplays } }) =>
          isCompleted && realizedReplays < availableReplays,
      );

  challengeResources.forEach((challengeResource) => {
    const { resource, config } = challengeResource;
    const { resourceDependencyList, behavior } = resource;
    const withoutDependencies = !resourceDependencyList?.length;
    let conditionSatisfied = true;

    if (!withoutDependencies) {
      conditionSatisfied = isDependentConditionSatisfied(
        resourceDependencyList,
        challengeResources,
      );
    }

    const isResourceAvailable = conditionSatisfied || withoutDependencies;

    if (isResourceAvailable) {
      config.isActive = true;
    } else {
      if (behavior == IResourceBehaviorsEnum.HIDDEN) {
        config.isVisible = false;
      }
      if (behavior == IResourceBehaviorsEnum.DISABLED) {
        config.isBlocked = true;
      }
    }
  });

  const isChallengeExpired = moment()
    .utcOffset(DEFAULT_TIMEZONE, false)
    .isAfter(moment.utc(endDate).utcOffset(DEFAULT_TIMEZONE, false));

  const isChallengeComingSoon = moment
    .utc(startDate)
    .utcOffset(DEFAULT_TIMEZONE, false)
    .isAfter(moment.utc(Date.now()).utcOffset(DEFAULT_TIMEZONE, false));

  const isChallengeOutOfStock = checkExperienceStock(challenge);

  challengePoints += mainEvent?.points || 0;

  switch (true) {
    case challengeEvents.length > 1:
      challengeModality = 'multi-event';
      break;
    case mainEvent?.status?.isOnline:
      challengeModality = 'online';
      break;
    case mainEvent?.status?.isOnsite:
      challengeModality = 'onSite';
      break;
    case challengeType === FORMATION:
      challengeModality = 'training';
      break;
  }

  if (!isExperience && !isContest && !isDonation && !isFormationCon) {
    switch (true) {
      case mainEvent?.status?.isCanceled:
        challengeStatusLabel = 'canceled';
        break;
      // case mainEvent?.status?.isFull:
      //   challengeStatusLabel = 'sold-out';
      //   break;
      // TODO: Funcionalidad nueva. API no preparada
      // case mainEvent?.status?.isInProgress:
      //   challengeStatusLabel = 'on-live';
      //   break;
      // case isLastPlaces:
      //   challengeStatusLabel = 'last-places';
      //   break;
      case featured:
        challengeStatusLabel = 'featured';
        break;
      case isChallengeExpired:
        challengeStatusLabel = 'expired';
        break;
      case moment()
        .utcOffset(DEFAULT_TIMEZONE, false)
        .diff(startVisDate, 'days') <= 3:
        challengeStatusLabel = 'new';
        break;
      case isChallengeComingSoon:
        challengeStatusLabel = 'coming-soon';
    }
  }

  if (isExperience || isDonation || isFormationCon) {
    switch (true) {
      case isChallengeComingSoon:
        challengeStatusLabel = isFormationCon
          ? 'formation_con-coming-soon-consumer'
          : 'coming-soon-consumer';
        break;
      case isChallengeExpired:
        challengeStatusLabel = 'expired';
        break;
      case isChallengeOutOfStock:
        challengeStatusLabel = 'out-of-stock-consumer';
    }
  }

  const isCampaignCompleted =
    !isExperience &&
    !isContest &&
    !isDonation &&
    !isFormationCon &&
    challengeResources.length &&
    challengeResources.every(({ config: { isCompleted } }) => isCompleted);

  const isExperienceCompleted =
    isExperience &&
    challengeResources.length &&
    challengeResources
      .filter((resource) =>
        [
          REDEEM_PRODUCT,
          CHECKOUT,
          CHECKOUT_LOTTERY,
          CHECKOUT_PAYMENT_GATEWAY,
          QUIZ_TYPEFORM,
          CHECKOUT_BOOKING_TIMIFY,
        ].includes(resource.type),
      )
      .every(({ config: { isCompleted } }) => isCompleted);

  const isDonationCompleted =
    isDonation &&
    challengeResources.length &&
    challengeResources
      .filter((resource) => [CHECKOUT_DONATION].includes(resource.type))
      .every(({ config: { isCompleted } }) => isCompleted);

  const isContestCompleted =
    isContest &&
    challengeResources.length &&
    challengeResources.filter((resource) =>
      [BEAUTY_GUIDE, QUIZ_TYPEFORM].includes(resource.type),
    ).length &&
    challengeResources
      .filter((resource) =>
        [BEAUTY_GUIDE, QUIZ_TYPEFORM].includes(resource.type),
      )
      .every(({ config: { isCompleted } }) => isCompleted);

  const isFormationConCompleted =
    isFormationCon &&
    challengeResources.length &&
    challengeResources
      .filter((resource) => [QUIZ_DERMO].includes(resource.type))
      .every(({ config: { isCompleted } }) => isCompleted);

  switch (true) {
    case mainEvent?.attend?.isPerformed:
      break;
    case mainEvent?.inscription?.isPerformed:
      challengeUserLabel = 'registered';
      break;
    case userStatus?.status === WAITING_LIST:
      challengeUserLabel = 'waiting-list';
      break;
    case headboardConfiguration.isQuizInprogress:
      challengeUserLabel = 'in-progress';
      break;
    case headboardConfiguration.isQuizDone && isFormationCon:
      // ? Label as completed when all the quizes are done and not just one
      if (isFormationConCompleted) {
        challengeUserLabel = 'formation_con-completed';
      } else {
        challengeUserLabel = 'in-progress';
      }
      break;
    case headboardConfiguration.isQuizDone:
      challengeUserLabel = 'quiz-done';
      break;
    case headboardConfiguration.isQuizDone &&
      challengeQuiz.dateDone?.isBefore(
        moment.utc(endDate).utcOffset(DEFAULT_TIMEZONE, false),
      ):
      challengeUserLabel = 'training-done';
      break;
    case isCampaignCompleted && !isChallengeReplayable:
      challengeUserLabel = 'campaign-completed';
      break;
    case isExperienceCompleted && !isChallengeReplayable:
      challengeUserLabel = 'experience-completed';
      break;
    case isContestCompleted && !isChallengeReplayable:
      challengeUserLabel = 'contest-completed';
      break;
    case isDonationCompleted && !isChallengeReplayable:
      challengeUserLabel = 'donation-completed';
      break;
    case isFormationConCompleted && !isChallengeReplayable:
      challengeUserLabel = 'formation_con-completed';
      break;
  }

  let challengeLikes = { totalLikes: 0, liked: false };
  if (likesList?.length) {
    const userHasLiked = likesList.filter(
      (_like) => _like.idUser.idUser === user.idUser,
    );

    challengeLikes = {
      ...challengeLikes,
      totalLikes: likesList.length,
      liked: !!userHasLiked.length,
    };
  }

  const isChallengeCompleted =
    isCampaignCompleted ||
    isExperienceCompleted ||
    isContestCompleted ||
    isDonationCompleted ||
    isFormationConCompleted ||
    userStatus?.status === DONE ||
    mainEvent?.attend?.isPerformed;

  const cardImage = isChallengeCompleted && endImage ? endImage : coverImage;

  const isVisible = isEventInProgress(
    challengeDates.startVisDate,
    challengeDates.endVisDate,
  );

  const challengeCategories: number[] =
    categories?.map((category) => category.idCategory) || [];

  const format = coverVideo?.trim().split('.');
  const isFromFile = format && isVideoValidFormat(format[format.length - 1]);
  const isCampaignActive =
    checkCampaignActive(challengeResources) && challengeType === CAMPAIGN;

  const isBlocked =
    user.blacklisted ||
    (challengeResources.some(
      (resource) => resource.type === ResourceTypeEnum.HIDDEN,
    ) &&
      !isChallengeAvailable(challenge.roles, user));

  challengeVideo = {
    url: coverVideo?.trim(),
    isFromFile: Boolean(coverVideo?.trim()) && isFromFile,
  };

  const challengeReplayConfig = {
    availableReplays: challenge.availableReplays,
    replayButtonDescriptions: challenge.replayButtonDescriptions,
    replayButtonTitles: challenge.replayButtonTitles,
    replayButtonUrl: challenge.replayButtonUrl,
  };

  return {
    active,
    isVisible,
    isBlocked,
    challengeId: idChallenge,
    challengeCode,
    challengeText,
    challengeVideo,
    challengeEvents,
    challengeFaqs,
    challengeGallery,
    challengePoints,
    challengePrice,
    challengeDonations,
    challengeBrand,
    challengeCategories,
    challengeType,
    challengeModality,
    challengeDates,
    challengeStatusLabel,
    challengeUserLabel,
    challengeCardButton: null,
    challengeListPath: null,
    challengeResources,
    challengeResourceGuide,
    disableHeader,
    disableImageHeader,
    showPoints,
    cardImage,
    challengeLikes,
    userStatus,
    headboardConfiguration,
    isCampaignActive,
    isChallengeCompleted,
    isChallengeReplayable: isChallengeReplayable,
    challengeSpeakers,
    cardImageMobile: coverImageMobile || cardImage,
    descriptionShort,
    specialties,
    challengeReplayConfig,
    roles,
    tags,
    //Old Data
    i18nList,
    name,
    description,
    descriptionLong,
  };
};
